import {
    CognitoUserPool,
    CognitoUser,
    CognitoUserAttribute,
    AuthenticationDetails,
    CognitoUserSession,
    CognitoIdToken,
    CognitoAccessToken,
    CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { COGNITO_CONFIG } from '../config/cognitoConfig';
import {
  userKeyFromClaims,
  hasCompletedOnboarding,
  hasSubmittedForApproval,
  getTenantStatusFromClaims,
  TENANT_STATUS_ACTIVE,
} from './onboarding';
import { clearCopilotClientData } from '../lib/copilotSessionStorage';
import { clearUploadQueue } from '../utils/uploadQueueStorage';
import { invalidateCache } from '../lib/copilotResponseCache';

export {
  markOnboardingComplete,
  markOnboardingSubmitted,
  isOnboardingComplete,
  hasCompletedOnboarding,
  hasSubmittedForApproval,
  getStoredTenantId,
  getStoredTenantName,
  userKeyFromClaims,
  clearOnboardingState,
  getTenantStatusFromClaims,
  TENANT_STATUS_ACTIVE,
  TENANT_STATUS_PENDING_APPROVAL,
  TENANT_STATUS_ONBOARDING,
  ADMIN_CONTACT_EMAIL,
} from './onboarding';

const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId
});

const createSessionFromAuthResult = (authResult) =>
    new CognitoUserSession({
        IdToken: new CognitoIdToken({ IdToken: authResult.IdToken }),
        AccessToken: new CognitoAccessToken({ AccessToken: authResult.AccessToken }),
        RefreshToken: new CognitoRefreshToken({ RefreshToken: authResult.RefreshToken }),
    });

const parseAuthResultFromError = (err) => {
    const raw = err?.message || (typeof err === 'string' ? err : '');
    if (!raw.includes('AuthenticationResult') && !raw.includes('IdToken')) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed.AuthenticationResult || parsed;
    } catch {
        return null;
    }
};

const persistUserSession = (user, session) => {
    user.setSignInUserSession(session);
    return { session, user };
};

const humanizeAuthError = (err) => {
    const raw = err?.message || '';
    if (raw.startsWith('{') || raw.includes('PASSWORD_VERIFIER') || raw.includes('AuthenticationResult')) {
        return new Error('No se pudo iniciar sesión. Verificá email y contraseña.');
    }
    return err instanceof Error ? err : new Error(raw || 'No se pudo iniciar sesión');
};

const authenticate = (user, authDetails, flowType) =>
    new Promise((resolve, reject) => {
        user.setAuthenticationFlowType(flowType);
        user.authenticateUser(authDetails, {
            onSuccess: (session) => resolve(persistUserSession(user, session)),
            onFailure: (err) => {
                const authResult = parseAuthResultFromError(err);
                if (authResult?.IdToken && authResult?.AccessToken) {
                    resolve(persistUserSession(user, createSessionFromAuthResult(authResult)));
                    return;
                }
                reject(humanizeAuthError(err));
            },
            newPasswordRequired: (userAttributes, requiredAttributes) => {
                reject(
                    Object.assign(new Error('Debés cambiar tu contraseña antes de continuar.'), {
                        code: 'NewPasswordRequired',
                        user,
                        userAttributes,
                        requiredAttributes,
                    })
                );
            },
            mfaRequired: (challengeName, challengeParameters) => {
                resolve({ challengeName, challengeParameters, user });
            },
        });
    });

/**
 * @param {string} email
 * @param {string} password
 * @param {{ given_name?: string, family_name?: string }} [attrs]
 */
export const signUp = (email, password, attrs = {}) => {
    const attributeList = [];
    const given = (attrs.given_name || "").trim();
    const family = (attrs.family_name || "").trim();
    if (given) {
        attributeList.push(new CognitoUserAttribute({ Name: "given_name", Value: given }));
    }
    if (family) {
        attributeList.push(new CognitoUserAttribute({ Name: "family_name", Value: family }));
    }
    return new Promise((resolve, reject) => {
        userPool.signUp(email, password, attributeList, null, (err, result) => {
            if (err) return reject(err);
            resolve(result);
        });
    });
};

export const signIn = async (email, password) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    try {
        return await authenticate(user, authDetails, 'USER_PASSWORD_AUTH');
    } catch (firstErr) {
        const msg = firstErr?.message || '';
        const passwordFlowDisabled =
            msg.includes('USER_PASSWORD_AUTH') ||
            msg.includes('not enabled') ||
            msg.includes('InvalidParameterException');
        if (!passwordFlowDisabled) {
            throw firstErr;
        }
        return authenticate(user, authDetails, 'USER_SRP_AUTH');
    }
};

export const forgotPassword = (email) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise((resolve, reject) => {
        user.forgotPassword({
            onSuccess: resolve,
            onFailure: reject,
            inputVerificationCode: resolve
        });
    });
};

export const passwordConfirmation = (email, code, newPassword) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise((resolve, reject) => {
        user.confirmPassword(code, newPassword, {
            onSuccess: resolve,
            onFailure: reject
        });
    });
};

export const confirmSignUp = (email, code) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise((resolve, reject) => {
        user.confirmRegistration(code, true, (err, result) => {
            if (err) return reject(err);
            resolve(result);
        });
    });
};

export const resendSignUpCode = (email) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise((resolve, reject) => {
        user.resendConfirmationCode((err, result) => {
            if (err) return reject(err);
            resolve(result);
        });
    });
};

/** Devuelve true si hay sesión Cognito válida. */
export const hasValidSession = () =>
    new Promise((resolve) => {
        const user = getCurrentUser();
        if (!user) return resolve(false);
        user.getSession((err, session) => {
            resolve(!err && session?.isValid());
        });
    });

export const getCurrentUser = () => userPool.getCurrentUser();

export const getIdTokenClaims = () =>
  new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("No current user"));
    user.getSession((err, session) => {
      if (err) return reject(err);
      if (!session?.isValid()) return reject(new Error("Session expired"));
      resolve(session.getIdToken().payload || {});
    });
  });

export const getTenantStatusFromSession = async () => {
  const claims = await getIdTokenClaims();
  return getTenantStatusFromClaims(claims);
};

export const forceRefreshSession = () =>
  new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("No current user"));
    user.getSession((err, session) => {
      if (err || !session?.isValid()) {
        reject(err || new Error("Session expired"));
        return;
      }
      const refreshToken = session.getRefreshToken();
      if (!refreshToken) {
        resolve(session);
        return;
      }
      user.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          resolve(session);
          return;
        }
        resolve(newSession);
      });
    });
  });

/** Refresca el IdToken si falta tenant_id, sigue en pending, falta custom:role o falta tenant_status. */
export const refreshSessionIfTenantPending = () =>
  new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("No current user"));
    user.getSession((err, session) => {
      if (err || !session?.isValid()) {
        reject(err || new Error("Session expired"));
        return;
      }
      const claims = session.getIdToken().payload || {};
      const tenantId = claims["custom:tenant_id"];
      const role = claims["custom:role"] || claims.role;
      const tenantStatus = getTenantStatusFromClaims(claims);
      const needsRefresh =
        !tenantId ||
        tenantId === PENDING_TENANT_ID ||
        !role ||
        role === "none" ||
        role === "pending" ||
        !tenantStatus;
      if (!needsRefresh) {
        resolve(session);
        return;
      }
      const refreshToken = session.getRefreshToken();
      if (!refreshToken) {
        resolve(session);
        return;
      }
      user.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          resolve(session);
          return;
        }
        resolve(newSession);
      });
    });
  });

export const getTenantIdFromSession = async () => {
  const claims = await getIdTokenClaims();
  return claims["custom:tenant_id"] || null;
};

const PENDING_TENANT_ID = "pending";

const onboardingWizardPath = () => {
  if (!sessionStorage.getItem("onboarding_profile")) {
    return "/onboarding/cuenta";
  }
  if (!sessionStorage.getItem("onboarding_tenant_id")) {
    return "/onboarding/workspace";
  }
  if (sessionStorage.getItem("onboarding_contact_done") !== "true") {
    return "/onboarding/contacto";
  }
  return "/onboarding/equipo";
};

/** Solo usuarios nuevos sin tenant asignado deben completar onboarding. */
export const needsOnboarding = (tenantId) => tenantId === PENDING_TENANT_ID;

/** Sin claim de tenant (pretokengen off o usuario sin fila TenantUsers). */
export const missingTenantClaim = (tenantId) => !tenantId;

/** Ruta post-auth según tenant (Home vs paso de onboarding). API = fuente de verdad. */
export const resolvePostLoginPath = async () => {
  try {
    const { fetchTenantAccessState } = await import("./tenantAccess");
    const claims = await getIdTokenClaims();
    const userKey = userKeyFromClaims(claims);
    let tenantId = claims["custom:tenant_id"] || null;

    if (needsOnboarding(tenantId) || missingTenantClaim(tenantId)) {
      await refreshSessionIfTenantPending();
      const freshClaims = await getIdTokenClaims().catch(() => claims);
      tenantId = freshClaims["custom:tenant_id"] || tenantId;
    }

    const state = await fetchTenantAccessState();
    if (state.approved) {
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return "/Home";
    }
    if (state.pending) {
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return "/solicitud-pendiente";
    }
    if (hasSubmittedForApproval(userKey) && !hasCompletedOnboarding(userKey)) {
      return "/solicitud-pendiente";
    }
    // JWT ya dice active (p. ej. myTenant falló pero pretokengen OK)
    const claimStatus = getTenantStatusFromClaims(claims);
    if (
      claimStatus === TENANT_STATUS_ACTIVE &&
      tenantId &&
      !needsOnboarding(tenantId)
    ) {
      return "/Home";
    }
    // Solo mandar al wizard si el tenant está en onboarding o el claim es pending/ausente
    if (state.wizard || needsOnboarding(tenantId) || missingTenantClaim(tenantId)) {
      if (process.env.NODE_ENV === "development") {
        console.warn("post-login → onboarding", {
          tenantId,
          claimStatus,
          approved: state.approved,
          pending: state.pending,
          wizard: state.wizard,
          error: state.error,
          tenantStatus: state.tenant?.status,
        });
      }
      return onboardingWizardPath();
    }
    // Tenant conocido pero estado ambiguo: no forzar onboarding
    return "/Home";
  } catch {
    return "/login";
  }
};

async function syncOnboardingFlagsFromTenant(tenant, userKey) {
  const { syncOnboardingFlagsFromTenant: sync } = await import("./tenantAccess");
  return sync(tenant, userKey);
}

/** Redirección automática desde /login si ya hay sesión. API = fuente de verdad. */
export const resolveLoginRedirectPath = async () => {
  try {
    const { fetchTenantAccessState } = await import("./tenantAccess");
    const userKey = userKeyFromClaims(await getIdTokenClaims());
    const state = await fetchTenantAccessState();

    if (state.approved) {
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return "/Home";
    }
    if (state.pending) {
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return "/solicitud-pendiente";
    }
    if (hasSubmittedForApproval(userKey) && !hasCompletedOnboarding(userKey)) {
      return "/solicitud-pendiente";
    }

    // Fallo de API / sin tenant: quedarse en /login (no empujar al wizard)
    if (state.error || !state.tenant) {
      return null;
    }

    const tenantId = await getTenantIdFromSession();
    if (state.wizard || needsOnboarding(tenantId)) {
      return onboardingWizardPath();
    }
    return null;
  } catch {
    return null;
  }
};

/** Marca onboarding completo solo si el tenant fue aprobado por admin. */
export const completeOnboardingForCurrentUser = async () => {
  const { fetchTenantAccessState, isTenantApprovedForApp, syncOnboardingFlagsFromTenant } =
    await import("./tenantAccess");
  const userKey = userKeyFromClaims(await getIdTokenClaims());
  const state = await fetchTenantAccessState();
  if (isTenantApprovedForApp(state.tenant)) {
    syncOnboardingFlagsFromTenant(state.tenant, userKey);
  }
};

export const canAccessApp = async () => {
  try {
    const { fetchTenantAccessState, syncOnboardingFlagsFromTenant } = await import(
      "./tenantAccess"
    );
    const state = await fetchTenantAccessState();
    if (state.approved) {
      const userKey = userKeyFromClaims(await getIdTokenClaims());
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const canAccessPendingApprovalPage = async () => {
  try {
    const { fetchTenantAccessState, syncOnboardingFlagsFromTenant } = await import(
      "./tenantAccess"
    );
    const state = await fetchTenantAccessState();
    if (state.approved) return false;

    const userKey = userKeyFromClaims(await getIdTokenClaims());
    if (state.pending) {
      syncOnboardingFlagsFromTenant(state.tenant, userKey);
      return true;
    }
    return hasSubmittedForApproval(userKey) && !hasCompletedOnboarding(userKey);
  } catch {
    return false;
  }
};

export const signOut = () => {
    const user = getCurrentUser();
    if (user) user.signOut();
    sessionStorage.removeItem("onboarding_profile");
    sessionStorage.removeItem("onboarding_password");
    sessionStorage.removeItem("onboarding_email_confirmed");
    clearCopilotClientData();
    clearUploadQueue();
    invalidateCache();
};

/** Cierra sesión y vuelve a autenticar para obtener JWT fresco (pretokengen). */
export const refreshAuthSession = async (email, password) => {
    signOut();
    return signIn(email, password);
};
